import React from "react";
import {
  Building03Icon,
  CreditCardIcon,
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import Box from "@mui/material/Box";
import { Avatar, PageContainer, StatCard } from "@outpave/ds";
import { useTranslation } from "react-i18next";
import {
  AddAccountAction,
  HeadlineMeta,
  SpendVelocityPanel,
  TopCompaniesPanel,
  type PlatformMetrics,
} from "components/platform/dashboard";
import ErrorState from "components/common/ErrorState";
import ProgramInsightsPanel from "components/common/ProgramInsightsPanel";
import TopCategoriesPanel from "components/common/TopCategoriesPanel";
import { Columns, Rows, Stack } from "components/layout/reusableLayouts";
import { usePlatformDashboard } from "hooks/usePlatformDashboard";
import { useIsSmallScreen } from "hooks/useIsSmallScreen";
import { formatCash } from "utils/formatCash";
import { formatNumber } from "utils/formatNumber";
import {
  pastDueShare,
  platformInsights,
} from "utils/platform/platformInsights";

/** The admin panel row's 360 to 512px band; both rows flex on a zero basis, so they stay equal in height. */
const ELASTIC_ROW_SX = {
  flex: { lg: 1 },
  minHeight: { lg: 360 },
  maxHeight: { lg: 512 },
};

const NO_METRICS: PlatformMetrics = {
  companies: { thisMonth: 0, lastMonth: 0, total: 0 },
  cards: { thisMonth: 0, lastMonth: 0, total: 0 },
  funds: {
    available: 0,
    incoming: 0,
    outgoing: 0,
    pastDue: 0,
    activeBalance: 0,
  },
  transactions: {
    healthPercent: 0,
    averageSize: 0,
    totalTransactions: 0,
    completedTransactions: 0,
  },
};

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const isSmallScreen = useIsSmallScreen();
  const {
    metrics,
    cashFlow,
    companies,
    topCategories,
    loading,
    metricsLoading,
    cashFlowLoading,
    topCategoriesLoading,
    topCompaniesLoading,
    error,
    refetch,
  } = usePlatformDashboard();

  // A failed background refetch keeps the cached page; only a page with no data at all shows the error.
  if (error && !loading && !metrics) {
    return (
      <PageContainer wrapped={false}>
        <ErrorState
          title={t("Something went wrong")}
          description={t("DashboardLoadError")}
          retryLabel={t("Retry")}
          onRetry={refetch}
        />
      </PageContainer>
    );
  }

  const {
    companies: companyCounts,
    cards,
    funds,
    transactions,
  } = metrics ?? NO_METRICS;

  const headlineCaption = ({
    thisMonth,
    lastMonth,
  }: PlatformMetrics["cards"]) => {
    const parts = [
      { figure: formatNumber(thisMonth), qualifier: t("newly added") },
    ];
    // With nothing last month there is no change to state.
    if (lastMonth) {
      const delta = thisMonth - lastMonth;
      const pct = formatNumber(Math.round(Math.abs(delta / lastMonth) * 100));
      parts.unshift({
        figure:
          delta > 0
            ? t("Up {{pct}}%", { pct })
            : delta < 0
              ? t("Down {{pct}}%", { pct })
              : t("No change"),
        qualifier: t("from last month"),
      });
    }
    return <HeadlineMeta parts={parts} />;
  };

  const money = (amount: number) => formatCash(amount) ?? "";

  return (
    <PageContainer wrapped={false}>
      <Stack gap="var(--space-4)" sx={{ flex: 1 }}>
        <Columns gap="var(--space-4)" count={{ xs: 1, md: 2 }}>
          <StatCard
            orientation="horizontal"
            leading={
              <Avatar
                size="lg"
                color="success"
                icon={<HugeiconsIcon icon={Building03Icon} size={16} />}
              />
            }
            title={t("Active Accounts")}
            value={formatNumber(companyCounts.total)}
            caption={metricsLoading ? "" : headlineCaption(companyCounts)}
            loading={metricsLoading}
          />
          <StatCard
            orientation="horizontal"
            leading={
              <Avatar
                size="lg"
                color="brand"
                icon={<HugeiconsIcon icon={CreditCardIcon} size={16} />}
              />
            }
            title={t("Total cards issued")}
            value={formatNumber(cards.total)}
            caption={metricsLoading ? "" : headlineCaption(cards)}
            loading={metricsLoading}
          />
        </Columns>

        <Columns gap="var(--space-4)" count={{ xs: 2, lg: 4 }}>
          <StatCard
            title={t("Cash Available")}
            value={money(funds.available)}
            caption={
              companyCounts.total === 1
                ? t("1 account")
                : t("{{number}} accounts", {
                    number: formatNumber(companyCounts.total),
                  })
            }
            tooltip={t("Total capital available across all accounts")}
            loading={metricsLoading}
          />
          <StatCard
            title={t("Total Incoming")}
            value={money(funds.incoming)}
            caption={t("This month")}
            tooltip={t("All funds received through the platform this month")}
            loading={metricsLoading}
          />
          <StatCard
            title={t("Total Outgoing")}
            value={money(funds.outgoing)}
            caption={t("This month")}
            tooltip={t("All funds spent through the platform this month")}
            loading={metricsLoading}
          />
          <StatCard
            title={t("Active Balance")}
            value={money(funds.activeBalance)}
            caption={
              cards.total === 1
                ? t("1 card")
                : t("{{number}} cards", { number: formatNumber(cards.total) })
            }
            tooltip={t("Funds currently in use across all accounts")}
            loading={metricsLoading}
          />
        </Columns>

        <Columns
          gap="var(--space-4)"
          count={{ xs: 1, lg: 3 }}
          sx={ELASTIC_ROW_SX}
        >
          <Stack sx={{ gridColumn: { lg: "span 2" }, minHeight: 0 }}>
            <SpendVelocityPanel cashFlow={cashFlow} loading={cashFlowLoading} />
          </Stack>
          <Rows count={2} gap="var(--space-4)" sx={{ minHeight: 0 }}>
            <StatCard
              sx={{ height: "100%" }}
              title={t("Past Due Balances")}
              value={
                <Box
                  component="span"
                  sx={{ color: "var(--color-text-danger)" }}
                >
                  {money(funds.pastDue)}
                </Box>
              }
              caption={t("{{percent}}% of active balance", {
                percent: pastDueShare(funds.pastDue, funds.activeBalance),
              })}
              tooltip={t("Outstanding balances past their due date")}
              loading={metricsLoading}
            />
            <Columns gap="var(--space-4)" count={2}>
              <StatCard
                sx={{ height: "100%" }}
                title={t("Avg Transaction")}
                value={money(transactions.averageSize)}
                caption={
                  transactions.totalTransactions === 1
                    ? t("Across 1 transaction")
                    : t("Across {{number}} transactions", {
                        number: formatNumber(transactions.totalTransactions),
                      })
                }
                tooltip={t("Average size per completed transaction this month")}
                loading={metricsLoading}
              />
              <StatCard
                sx={{ height: "100%" }}
                title={t("Health Score")}
                value={
                  <Box
                    component="span"
                    sx={{ color: "var(--color-text-success)" }}
                  >
                    {`${transactions.healthPercent}%`}
                  </Box>
                }
                caption={t("{{number}} completed this month", {
                  number: formatNumber(transactions.completedTransactions),
                })}
                tooltip={t("% of successful transactions this month")}
                loading={metricsLoading}
              />
            </Columns>
          </Rows>
        </Columns>

        <Columns
          gap="var(--space-4)"
          count={{ xs: 1, lg: 3 }}
          sx={ELASTIC_ROW_SX}
        >
          <TopCategoriesPanel
            categories={topCategories.map((category) => ({
              id: category.name,
              label: category.name,
              value: category.totalSpend,
            }))}
            loading={topCategoriesLoading}
            showRemainder
          />
          <ProgramInsightsPanel
            insights={metrics ? platformInsights(metrics, t) : []}
            loading={metricsLoading}
          />
          <TopCompaniesPanel
            companies={companies}
            loading={topCompaniesLoading}
          />
        </Columns>

        {isSmallScreen && <AddAccountAction fab />}
      </Stack>
    </PageContainer>
  );
};

export default Dashboard;
